import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/pages/auth/login';
import Register from './components/pages/auth/register';
import TestSuiteCreation from './components/pages/testsuites/testsuite';
import TestSuiteList from './components/pages/testsuites/TestSuiteList';
import CreateTestCase from './components/pages/testcases/createTestCase';
import TestCaseList from './components/pages/testcases/TestCaseList';
import JobList from './components/pages/jobs/JobsList';
import JobTestCaseList from './components/pages/jobs/JobTestCase';
import EditTestCase from './components/pages/testcases/EditTestCase';
import WorkflowList from './components/pages/workflows/WorkflowList';
import CreateWorkflow from './components/pages/workflows/createWorkflow';
import EditWorkflow from './components/pages/workflows/EditWorkflow';
import EditTestSuite from './components/pages/testsuites/EditTestSuite';
import UserProfilePage from './components/pages/settings/UserProfilePage';
import IntegrationsPage from './components/pages/settings/IntegrationsPage';
import JiraAuthSuccess from './components/pages/settings/JiraAuthSuccess';
import ApiKeysPage from './components/pages/settings/ApiKeysPage';
import TestDataPage from './components/pages/testdata';
import CreateTestData from './components/pages/testdata/CreateTestData';
import TestDataDetails from './components/pages/testdata/TestDataDetails';
import YamlEditorPage from './components/pages/testdata/YamlEditorPage';
import { isTokenValid, clearAuthData } from './utils/auth';
import ApiCallsList from './components/pages/api/ApiCallsList';
import CreateApiCall from './components/pages/api/CreateApiCall';
import EditApiCall from './components/pages/api/EditApiCall';
import { ToastProvider } from './components/ui/use-toast.jsx';


// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  // Use the more robust token validation function
  const isValid = isTokenValid();
  
  if (!isValid) {
    // Save the current path to redirect back after login
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/register') {
      sessionStorage.setItem('redirectUrl', currentPath);
    }
    
    // Clear any potentially invalid tokens
    clearAuthData();
    
    // Redirect to login if there's no valid token
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Home route component to handle root URL redirection
const HomeRoute = () => {
  // Use the more robust token validation function
  const isValid = isTokenValid();
  
  if (isValid) {
    // Check if there's a saved redirect URL
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      // Clear the stored URL
      sessionStorage.removeItem('redirectUrl');
      return <Navigate to={redirectUrl} replace />;
    }
    // Default redirect to test suites page if logged in and no specific URL saved
    return <Navigate to="/testsuites" replace />;
  }
  
  // Clear any potentially invalid tokens
  clearAuthData();
  
  // Redirect to login if not logged in
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <Router>
      <ToastProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Jira OAuth2 Success Page - semi-protected */}
          <Route path="/jira-auth-success" element={<JiraAuthSuccess />} />

          {/* Root route - redirects based on auth state */}
          <Route path="/" element={<HomeRoute />} />

          <Route 
            path="/me" 
            element={
              <ProtectedRoute>
                <UserProfilePage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/settings/api-keys"
            element={
              <ProtectedRoute>
                <ApiKeysPage />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/me/integrations" 
            element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } 
          />

          {/* Test Data routes */}
          <Route
            path="/testdata"
            element={
              <ProtectedRoute>
                <TestDataPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/testdata/create"
            element={
              <ProtectedRoute>
                <CreateTestData />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/testdata/:id"
            element={
              <ProtectedRoute>
                <TestDataDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/testdata/cleanup"
            element={
              <ProtectedRoute>
                <YamlEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Test Suites routes */}
          <Route
            path="/testsuites"
            element={
              <ProtectedRoute>
                <TestSuiteList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/testsuites/create"
            element={
              <ProtectedRoute>
                <TestSuiteCreation />
              </ProtectedRoute>
            }
          />

          <Route
            path="/testsuites/:id/edit"
            element={
              <ProtectedRoute>
                <EditTestSuite />
              </ProtectedRoute>
            }
          />

          <Route
            path="/testsuites/:suiteId/tests"
            element={
              <ProtectedRoute>
                <TestCaseList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/testsuites/:suiteId/tests/create"
            element={
              <ProtectedRoute>
                <CreateTestCase />
              </ProtectedRoute>
            }
          />

          <Route
            path="/job/:jobId"
            element={
              <ProtectedRoute>
                <JobTestCaseList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <JobList />
              </ProtectedRoute>
            }
          />

          <Route path="/testcase/:testCaseId/edit" element={
            <ProtectedRoute>
              <EditTestCase />
            </ProtectedRoute>
          } />

          <Route path="/testsuites/:suiteId/workflows" element={<WorkflowList />} />
          <Route path="/testsuites/:suiteId/workflows/create" element={<CreateWorkflow />} />

          <Route 
            path="/workflows/:workflowId/edit" 
            element={
              <ProtectedRoute>
                <EditWorkflow />
              </ProtectedRoute>
            } 
          />

          {/* Test Case Routes */}
          <Route path="/testsuites/:suiteId/create" element={<ProtectedRoute><CreateTestCase /></ProtectedRoute>} />
          
          {/* API Call Routes */}
          <Route path="/api-calls" element={<ProtectedRoute><ApiCallsList /></ProtectedRoute>} />
          <Route path="/api-calls/create" element={<ProtectedRoute><CreateApiCall /></ProtectedRoute>} />
          <Route path="/api-calls/edit/:id" element={<ProtectedRoute><EditApiCall /></ProtectedRoute>} />

          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App;
